/**
 * «حذفِ بخشی از داده‌ها، بدونِ حذفِ حساب» — قرینه‌ی
 * apps/mobile/src/features/settings/EraseDataSection.tsx.
 *
 * فهرست، ترتیب و متن‌ها از `core/lib/dataErase` می‌آیند و خودِ پاک کردن از
 * `core/data/dataEraseRepo`؛ این فایل فقط رابط است.
 *
 * هر ردیف تاییدِ **خودش** را می‌گیرد. یک تاییدِ کلی برای «همه‌ی این‌ها»
 * وسوسه‌انگیز بود ولی دقیقاً همان چیزی است که کاربر نمی‌خواهد: کسی که
 * آمده عکسش را بردارد نباید یک لغزشِ انگشت تا پاک شدنِ دفترچه‌اش فاصله
 * داشته باشد.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dataEraseRepo } from '@/core/data/dataEraseRepo';
import { NO_ERASABLE_DATA, eraseRows, type ErasableKind } from '@/core/lib/dataErase';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Card } from '@/ui/kit';

export function EraseDataSection() {
  useLocale();
  const qc = useQueryClient();
  const { session, refreshProfile } = useSession();
  const [confirming, setConfirming] = useState<ErasableKind | null>(null);
  const [done, setDone] = useState<ErasableKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * `enabled` شرطِ لازم است و نه احتیاطِ اضافی: صفحه‌ی حذفِ حساب وسطِ کارش
   * عمداً کاربر را خارج می‌کند (تاییدِ حذف)، و بدونِ این شرط همین کوئری
   * بلافاصله دوباره می‌دود و شش درخواستِ محکوم‌به‌۴۰۳ می‌فرستد.
   */
  const counts = useQuery({
    queryKey: ['eraseCounts'],
    queryFn: dataEraseRepo.counts,
    enabled: !!session,
  });

  const erase = useMutation({
    mutationFn: (kind: ErasableKind) => dataEraseRepo.erase(kind),
    onSuccess: async (_r, kind) => {
      setConfirming(null);
      setDone(kind);
      setError(null);
      /**
       * خودِ فهرست، و هر جای دیگری که همین داده را نشان می‌دهد.
       *
       * `refreshProfile` مهم‌ترینشان است و از کشِ react-query نمی‌آید:
       * عکسِ پروفایل در سربرگ از نشست خوانده می‌شود، پس بدونِ این، کاربر
       * عکسی را می‌بیند که همین حالا پاکش کرده.
       */
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['eraseCounts'] }),
        qc.invalidateQueries({ queryKey: ['myPhones'] }),
        qc.invalidateQueries({ queryKey: ['contactFriends'] }),
        // سه‌تای گروهِ دوم چند فهرست را هم‌زمان کهنه می‌کنند: سیگنالِ لغوشده
        // در «مالِ من» و در فیدِ دوستان و در چتِ همان سیگنال دیده می‌شود.
        qc.invalidateQueries({ queryKey: ['mine'] }),
        qc.invalidateQueries({ queryKey: ['incoming'] }),
        qc.invalidateQueries({ queryKey: ['myEvents'] }),
        qc.invalidateQueries({ queryKey: ['events'] }),
        qc.invalidateQueries({ queryKey: ['messages'] }),
        refreshProfile(),
      ]);
    },
    onError: (e) => {
      setConfirming(null);
      reportError('web.dataErase', e);
      setError(t('errorRetry'));
    },
  });

  const rows = eraseRows(counts.data ?? NO_ERASABLE_DATA);
  const confirmRow = rows.find((r) => r.kind === confirming) ?? null;

  return (
    <>
      <h3>{t('eraseTitle')}</h3>
      <p className="dim">{t('eraseSub')}</p>

      <Card>
        {rows.map((row, i) => (
          <React.Fragment key={row.kind}>
            {/* یک‌بار، درست بالای اولین ردیفی که بقیه هم دیده‌اندش: قاعده‌ی
                این گروه فرق دارد و باید **قبل** از دکمه گفته شود. */}
            {row.affectsOthers && !rows[i - 1]?.affectsOthers
              ? <p className="dim">{t('eraseOthersSaw')}</p> : null}
            <div className="phone-row">
              <div>
                <b>{t(row.titleKey)}</b>
                <p className="dim">
                  {/* تا وقتی شمارش نرسیده، «چیزی برای حذف نیست» دروغ است —
                      پس همان‌جا سه‌نقطه می‌نشیند و ردیف غیرفعال می‌ماند. */}
                  {counts.isLoading ? '…' : t(row.stateKey, row.params)}
                </p>
                {done === row.kind ? <p className="field-note ok">{t('eraseDone')}</p> : null}
              </div>
              <button type="button" className="btn"
                data-testid={`erase-${row.kind}`}
                disabled={counts.isLoading || row.disabled || erase.isPending}
                onClick={() => { setDone(null); setConfirming(row.kind); }}>
                {erase.isPending && erase.variables === row.kind
                  ? t('eraseWorking') : t('eraseBtn')}
              </button>
            </div>
          </React.Fragment>
        ))}
      </Card>

      {error ? <p className="error">{error}</p> : null}

      {confirmRow ? (
        <div className="modal-backdrop" onClick={() => setConfirming(null)}>
          <div className="card modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t(confirmRow.confirmKey)}</h3>
            <button type="button" className="btn danger-solid block" disabled={erase.isPending}
              data-testid="erase-confirm"
              onClick={() => erase.mutate(confirmRow.kind)}>
              {erase.isPending ? t('eraseWorking') : t('eraseConfirmYes')}
            </button>
            <button type="button" className="btn ghost block" onClick={() => setConfirming(null)}>
              {t('eraseKeep')}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
